import { useEffect, useState } from 'react';

/**
 * Runs `callback` two frames from now: past the frame the current commit
 * paints and past whatever work it set off (a mounting view's effects and
 * the renders they schedule), so motion started there is not spent behind
 * a busy main thread before the first frame of it is drawn. Returns the
 * cancel.
 */
export function afterSettle(callback) {
    let second;
    const first = requestAnimationFrame(() => {
        second = requestAnimationFrame(callback);
    });
    return () => {
        cancelAnimationFrame(first);
        cancelAnimationFrame(second);
    };
}

/**
 * `value`, as it was until two frames after it last changed (afterSettle).
 * For what should change visibly, through a CSS transition, once a heavy
 * update has landed rather than inside it. What a screen reader or a test
 * reads should stay on the live value.
 */
export default function useSettledValue(value) {
    const [settled, setSettled] = useState(value);

    useEffect(() => {
        if (Object.is(settled, value)) return undefined;
        return afterSettle(() => setSettled(value));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only a new `value` restarts the wait.
    }, [value]);

    return settled;
}
